import React, { useEffect } from 'react'
import { useAppTranslation } from '../../../hooks/useAppTranslation'
import { useToastNotifications } from '../../../hooks/useToastNotification'
import { useUploadMedia } from '../../../hooks/useUploadMedia'
import { useUploadDocument } from '../../../hooks/useUploadDocument'
import { downloadDocumentFile } from '../../../api/documents'
import { ToastType } from '../../common/Toast/toast-type'
import { DropZone, DropZoneVariant } from '../../common/DropZone'
import { FormControl } from '../../common/FormControl'
import { Image } from '../../common/Image'
import { PostThumbnails } from '../../feed/AttachmentsContainer/PostThumbnails'
import { Thumbnail } from '../../feed/AttachmentsContainer/Thumbnail'

const isServerId = (id: string | undefined): id is string =>
  !!id && !id.startsWith('blob:') && !id.startsWith('http')

export interface TicketAttachmentsValue {
  mediaIds: string[]
  documentIds: string[]
  isUploading: boolean
}

interface TicketAttachmentsProps {
  societyId: string
  onChange: (value: TicketAttachmentsValue) => void
}

export const TicketAttachments = ({
  societyId,
  onChange,
}: TicketAttachmentsProps): React.ReactNode => {
  const { translate } = useAppTranslation()
  const { setToastNotification } = useToastNotifications()

  const {
    imageUploading,
    imageUploadDisabled,
    onImageRemove,
    uploadFiles: uploadImageFiles,
    messageImagesState,
  } = useUploadMedia()

  const {
    documentUploading,
    documentUploadDisabled,
    onDocumentRemove,
    uploadFiles: uploadDocumentFiles,
    documentsState,
  } = useUploadDocument({ societyId, section: 'ticket' })

  useEffect(() => {
    onChange({
      mediaIds: messageImagesState.images.flatMap((img) =>
        isServerId(img.id) ? [img.id] : []
      ),
      documentIds: documentsState.documents.flatMap((doc) =>
        isServerId(doc.id) ? [doc.id] : []
      ),
      isUploading: imageUploading || documentUploading,
    })
  }, [
    messageImagesState,
    documentsState,
    imageUploading,
    documentUploading,
    onChange,
  ])

  const downloadDocument = (documentId: string, filename: string): void => {
    downloadDocumentFile(documentId, filename)
  }

  return (
    <>
      <div>
        <FormControl
          label={translate('ticketView.form.labels.images')}
          name="images"
        >
          <DropZone
            multiple
            variant={DropZoneVariant.MEDIA}
            disabled={imageUploadDisabled}
            onAcceptedFiles={uploadImageFiles}
            onUpload={(status) => {
              if (status === 'error') {
                setToastNotification(
                  ToastType.DANGER,
                  translate('flashMessage.somethingWentWrongError')
                )
              }
            }}
          />
        </FormControl>

        {messageImagesState.images.length > 0 && (
          <PostThumbnails header={translate('ticketView.attachments.images')}>
            <div className="flex flex-wrap gap-3 px-2 pt-3">
              {messageImagesState.images.map((_image) => (
                <div key={_image.uri} className="h-12 w-12">
                  <Image
                    url={_image.uri}
                    onDeleteClick={
                      _image.uploading ? undefined : () => onImageRemove(_image)
                    }
                    shouldCloseTopRight
                  />
                </div>
              ))}
            </div>
          </PostThumbnails>
        )}
      </div>

      <div>
        <FormControl
          label={translate('ticketView.form.labels.documents')}
          name="documents"
        >
          <DropZone
            multiple
            variant={DropZoneVariant.DOCUMENT}
            disabled={documentUploadDisabled}
            onAcceptedFiles={uploadDocumentFiles}
            onUpload={(status) => {
              if (status === 'error') {
                setToastNotification(
                  ToastType.DANGER,
                  translate('flashMessage.somethingWentWrongError')
                )
              }
            }}
          />
        </FormControl>

        {documentsState.documents.length > 0 && (
          <PostThumbnails
            header={translate('ticketView.attachments.documents')}
          >
            {documentsState.documents.map((doc, index) => (
              <Thumbnail
                key={`${doc.uri}${doc.id}${doc.name}`}
                id={(doc.id as string) || doc.uri}
                icon={doc.icon}
                title={doc.name || `File-${index}`}
                description={doc.size || 'Unknown file size'}
                index={index}
                onThumbnailClick={() => {
                  if (doc.id && !doc.uploading) {
                    downloadDocument(doc.id, doc.name)
                  }
                }}
                onThumbnailRemove={onDocumentRemove}
                loading={doc.uploading}
              />
            ))}
          </PostThumbnails>
        )}
      </div>
    </>
  )
}
